import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, ButtonVariants } from '@carboledger/ui';
import { Plus, FileUp } from '@carboledger/ui/icons';
import { ROUTES } from '@/app/routes';
import { buildTableLabels } from '@/config/tableLabels';
import { useTableState, useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { useGetSitesQuery } from '../../api/sitesApi';
import { buildSitesColumns } from '../../constants/sitesColumns';
import { AssignMaterialsModal } from './AssignMaterialsModal';

const getSiteId = (row) => row.id;

export const SitesTable = () => {
  const t = useTranslations();
  const navigate = useNavigate();

  const [assignToSiteId, setAssignToSiteId] = useState(null);

  const table = useTableState();
  const { data, isFetching } = useGetSitesQuery(table.queryParams);
  const rows = data?.rows ?? EMPTY_LIST;

  const handleAdd = useCallback(() => {
    navigate(ROUTES.siteCreate());
  }, [navigate]);

  const handleEdit = useCallback((row) => navigate(ROUTES.siteEdit(row.id)), [navigate]);

  const handleOpenSite = useCallback((row) => navigate(ROUTES.siteDetail(row.id)), [navigate]);

  const handleImportSites = useCallback(() => {}, []);
  const handleImportTerminals = useCallback(() => {}, []);
  const handleAssignMaterials = useCallback((row) => setAssignToSiteId(row.id), []);
  const handleCloseAssignMaterials = useCallback(() => setAssignToSiteId(null), []);

  const columns = useMemo(
    () =>
      buildSitesColumns(t, {
        serialOffset: (table.page - 1) * table.pageSize,
        onOpenSite: handleOpenSite,
      }),
    [t, table.page, table.pageSize, handleOpenSite],
  );

  const handleSearchChange = useCallback(
    (value) => {
      table.setSearch(value);
      table.setPage(1);
    },
    [table],
  );

  const toolbarActions = useMemo(
    () => [
      {
        id: 'import-terminals',
        label: t('SITES_IMPORT_TERMINALS'),
        icon: FileUp,
        variant: ButtonVariants.secondary,
        disabled: true,
        onClick: handleImportTerminals,
      },
      {
        id: 'import-sites',
        label: t('SITES_IMPORT'),
        icon: FileUp,
        variant: ButtonVariants.secondary,
        disabled: true,
        onClick: handleImportSites,
      },
      {
        id: 'add-site',
        label: t('SITES_ADD'),
        icon: Plus,
        variant: ButtonVariants.primary,
        onClick: handleAdd,
      },
    ],
    [t, handleImportTerminals, handleImportSites, handleAdd],
  );

  const rowActions = useMemo(
    () => [
      {
        id: 'assign-materials',
        label: t('SITES_ACTION_ASSIGN_MATERIALS'),
        variant: ButtonVariants.tertiary,
        onClick: handleAssignMaterials,
      },
      {
        id: 'edit',
        label: t('COMMON_EDIT'),
        variant: ButtonVariants.tertiary,
        onClick: handleEdit,
      },
    ],
    [t, handleAssignMaterials, handleEdit],
  );

  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('SITES_EMPTY_TITLE'),
      description: t('SITES_EMPTY_DESCRIPTION'),
    }),
    [t],
  );

  const noResults = useMemo(
    () => ({
      title: t('SITES_NO_RESULTS_TITLE'),
      description: t('COMMON_NO_RESULTS_DESCRIPTION'),
    }),
    [t],
  );

  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  return (
    <>
      <Table
        columns={columns}
        data={rows}
        total={data?.totalCount ?? 0}
        loading={isFetching}
        getRowId={getSiteId}
        searchable
        onSearchChange={handleSearchChange}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        toolbarActions={toolbarActions}
        rowActions={rowActions}
        actionsHeader={t('SITES_COL_ACTIONS')}
        emptyState={emptyState}
        noResults={noResults}
        labels={tableLabels}
      />

      <AssignMaterialsModal
        open={assignToSiteId !== null}
        onClose={handleCloseAssignMaterials}
        siteId={assignToSiteId}
      />
    </>
  );
};
